import { Component, computed, signal } from "@angular/core"
import { TourScrollToElementComponent } from "../../../tour/tour-scroll-to-element.component"
import { IntroAnchor } from "../../../tour/tour.model"
import { CarouselComponent } from "../carousel/carousel.component"
import { UiInheritDirective } from "ui-inherit"
import { UiIconComponent } from "ui-icon"
import { faArrowUpRightFromSquare, faInfoCircle } from "@fortawesome/free-solid-svg-icons"
import { HelpTipsComponent } from "feature/redline/help-tips"

@Component({
  imports: [
    TourScrollToElementComponent,
    CarouselComponent,
    UiInheritDirective,
    UiIconComponent,
    HelpTipsComponent,
  ],
  standalone: true,
  template: `
    @if (showHelpTips()) {
      <lib-feature-help-tips (cancelAndClose)="showHelpTips.set(false)"/>
    }
    <div class="row gap-40">
      <div class="column gap-5 flex-1 min-w-[300px]">
        <div>
          Redlining turned the false idea of racial hierarchy into legal code and connected it to geography and
          property value, producing very real structural situations that remain today.
        </div>
        <div>
          Federal Redlining is among many policies and practices that deliberately created structural inequality that
          still exists today. Starting with redlining helps us understand how false ideas of racial hierarchy got
          baked into many of our systems and structures. Though policies like redlining were made illegal and formally
          ended, their structural legacies have never been undone.
        </div>

        <e2e-tour-scroll-to-element [elementName]="IntroAnchor.WHAT_IS_REDLINING"/>

        <div class="-ml-6 leading-8">
          <div class="text-3xl font-black">
            WHAT IS
          </div>
          <div class="text-5xl font-black text-accent">
            REDLINING?
          </div>
        </div>
        <div class="font-semibold">
          Federal redlining was rolled out as part of the New Deal. By using explicitly racist language to evaluate
          the creditworthiness of neighborhoods, it led to segregation and disinvestment for people of color, even
          while homeownership built middle-class wealth for White families.
        </div>
        <div>
          As the Federal Government swooped in to save the housing market, they invented the 15 and 30-year mortgage,
          insured home loans and transformed homeownership into a middle-class wealth building tool. This was before you
          or I would have a credit score, and instead they essentially gave everyone’s neighborhood a credit score.
          There
          were only four types, or ‘grades.’ Everybody got a color: green for the best areas; blue for second best;
          yellow
          was often referred to as declining; and red was used for ‘hazardous’ areas. Each neighborhood’s rating would
          become crucial for the property value and financial success of that area. Among the most important reasons to
          color an area red was the race of the people living there.
        </div>
      </div>
      <div
        style="width: 400px; height: 500px"
        class="column-center flex-1 italic text-sm min-w-[300]"
      >
        <e2e-carousel inherit/>
        <div>
          This virtual experience is based on the award-winning traveling analog exhibit of the same name:
          <a
            class="btn btn-secondary btn-xs"
            href="https://designingthewe.com/undesign-the-redline"
            target="_blank"
          >
            Undesign the Redline by Designing the We
            <lib-ui-icon [icon]="faArrowUpRightFromSquare"/>
          </a>
          which still visits communities across the nation to learn, activate, and mobilize us into a strong “WE”
          capable of transformation. We think the exhibit should go everywhere. To learn more about the folks and
          organizations who made this web application, please click the
          <button
            class="btn btn-accent btn-xs text-primary-content"
            (click)="showHelpTips.set(!showHelpTips())"
            [ariaLabel]="ariaLabel()"
            [attr.aria-expanded]="showHelpTips()"
          >
            <lib-ui-icon [icon]="faInfoCircle"/>
          </button>
          icon at the top right of this introduction page.
        </div>
      </div>

    </div>
  `,
  selector: "e2e-definitions",
})
export class DefinitionsComponent {
  protected readonly IntroAnchor = IntroAnchor
  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare
  protected readonly faInfoCircle = faInfoCircle

  showHelpTips = signal(false)

  ariaLabel = computed(() =>
    this.showHelpTips() ? "Hide help tips" : "Show help tips",
  )

}
